import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useCollection } from "../hooks/useCollection";
import { useCallableAction, callableErrorMessage } from "../hooks/useCallableAction";
import { useToast } from "../components/Toast";
import { LoadingBlock, Spinner } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { n } from "../lib/format";
import {
  cuttingLineItemId,
  parseSizeFromTitle,
  referenceSizeLabel,
  type CuttingLineFields,
} from "../lib/productionFields";
import { StatusCountCards, statusOrder, type StatusDoc } from "./Production";

/**
 * Kesim listesi — eski `cuttinglist.php`nin karşılığı.
 *
 * Bir üretim statüsündeki tüm sipariş kalemleri tek tabloda toplanır; her satıra
 * ölçü (size1 × size2), adet, kalınlık ve malzeme kaynağı girilir, yazdırılıp
 * atölyeye verilir.
 *
 * KORUNAN DAVRANIŞ
 * · Kesim adedi sipariş adedinden BAĞIMSIZDIR (1 masa için 4 ayak, 1 tabla).
 *   Sipariş adedi referanstır; ön dolu gelen değer ÖNERİDİR, kesinleşmiş değil.
 * · Türkçe virgüllü ondalık kabul edilir, kayıtta sayıya çevrilir.
 * · Yazdırmada girdi alanları gizlenir, değerler düz metin basılır.
 * · SSH ve stok siparişi kaynaklı üretimler kesim listesine girmez.
 *
 * DÜZELTİLEN DAVRANIŞ
 * · Kalınlık ve malzeme kaynağı serbest metin değil; sözleşmedeki değerler
 *   (0/18/25/40 · plaka/stok/parca) dışına çıkılamaz — sunucu da doğrular.
 * · Kaydedilmeyen satır SESSİZCE atlanmaz; ekranda gerekçesiyle işaretlenir.
 *   Eski `post.php` "size1 ve quantity doluysa yaz" kuralını sessizce uyguluyordu.
 * · Kaç kaydın kesim dışı olduğu (SSH/stok siparişi) kullanıcıya yazılır.
 *
 * ── ÖN DOLDURMA (2026-08-14) ───────────────────────────────────────────────
 * Ekran açıldığında HİÇBİR alan dolu gelmiyordu: ölçü, adet, mm ve malzeme
 * kutularının hepsi boş. Operatör 5 kutuyu satır satır sıfırdan dolduruyordu ve
 * canlıda BUGÜNE KADAR TEK BİR KESİM SATIRI KAYDEDİLMEMİŞTİ
 * (`auditLogs` içinde `cuttingList.save` = 0 kayıt; alt koleksiyondaki 10 satır
 * göç betiğinden geliyor). Ekran fiilen kullanılmıyordu.
 *
 * Artık:
 *   · ADET → sipariş adedinden ön dolar.
 *   · ÖLÇÜ → ürün adındaki ölçü metninden çıkarılabiliyorsa ön dolar
 *            ("Bely 3 Raflı Kitaplık - 180 x 38 cm" → 180 × 38). Canlı örneklemde
 *            4.000 kalemin 3.316'sında ad ölçü taşıyor; `optionSize` ve
 *            `variantTitle` ise SIFIR kalemde dolu.
 *   · mm ve MALZEME KAYNAĞI → ön DOLMAZ. İkisi de üründen türetilemez;
 *            uydurulmuş bir varsayılan yanlış plakanın kesilmesi demektir.
 *
 * ★ ÖNERİ ≠ KESİNLEŞMİŞ DEĞER. Ön dolu kutular sarı çerçeveli ve "öneri"
 * etiketlidir, serbestçe değiştirilir ve — en önemlisi — OPERATÖR O SATIRA
 * DOKUNMADAN KAYDEDİLMEZ. Kaydet düğmesi yalnızca bu oturumda düzenlenen
 * satırları gönderir; malzeme kaynağı zaten zorunlu ve hiç ön dolmadığı için
 * her satır en az bir bilinçli seçim gerektirir. Yazdırılan listede de öneri
 * değerlerin yanında "(öneri)" yazar.
 */

const CALLABLE_SAVE = "saveCuttingList";

/** Security Rules'ta sipariş kalemleri `orders/{id}/items` altındadır. */
/** Sipariş kalemleri. Shopify terminolojisi; indeksler de bu adla tanımlı. */
const ITEMS_SUBCOLLECTION = "lineItems";

/**
 * Kalınlık seçenekleri sözleşmeden gelir (Security Rules: thickness in [0,18,25,40]).
 * Eski ekranda yalnızca 18 ve 25 vardı; 40 mm panel sonradan eklendi.
 */
const THICKNESS_OPTIONS = [
  { value: 0, label: "Seç" },
  { value: 18, label: "18 mm" },
  { value: 25, label: "25 mm" },
  { value: 40, label: "40 mm" },
] as const;

const ORIGIN_OPTIONS = [
  { value: "plaka", label: "Plakadan" },
  { value: "stok", label: "Stoktan" },
  { value: "parca", label: "Parçadan" },
] as const;

type Origin = (typeof ORIGIN_OPTIONS)[number]["value"];

interface Row {
  key: string;
  productionId: string;
  orderId: string;
  orderLabel: string;
  orderItemId: string;
  /** Mevcut kesim dokümanı — varsa güncellenir. */
  docId?: string;
  productName: string;
  /** Referans ölçü etiketi (kesim ölçüsü DEĞİL). */
  refSize: string;
  /** Referans ölçü ürün adından çıkarıldıysa true — kaynağı ekranda yazılır. */
  refSizeFromTitle: boolean;
  orderQuantity: number;
  size1: string;
  size2: string;
  quantity: string;
  thickness: number;
  originOfMaterial: Origin | "";
  /** Ölçü kutuları ürün adından ÖNERİLDİ (kayıtlı değer değil). */
  suggestedSize: boolean;
  /** Adet kutusu sipariş adedinden ÖNERİLDİ. */
  suggestedQuantity: boolean;
  /** Operatör bu satırda bir kutuya dokundu mu? Öneri tek başına onay değildir. */
  dirty: boolean;
}

/** Kalemi okunamayan üretim kaydı — kesim satırı üretilemez, sebebi yazılır. */
interface SourcelessProduction {
  productionId: string;
  orderId: string;
  label: string;
}

/** `saveCuttingList` girdi satırı — sunucudaki zod şemasıyla birebir. */
interface SaveLine {
  id?: string;
  orderItemId: string;
  size1: number;
  size2: number;
  quantity: number;
  thickness: number;
  originOfMaterial: Origin;
}

interface SaveResult {
  saved?: number;
  results?: Array<{ orderItemId?: string; ok: boolean; error?: string }>;
  message?: string;
}

/**
 * Türkçe ondalık girişini sayıya çevirir ("12,5" → 12.5).
 * `pages/Components.tsx` içindeki `parseDecimal` ile aynı davranış; burada
 * yerel tutuldu ki kesim ekranı başka bir ekranın iç yapısına bağlanmasın.
 */
function parseDecimalTr(input: string): number | null {
  const raw = input.trim().replace(/\s/g, "");
  if (!raw) return null;
  const v = Number(raw.replace(",", "."));
  return Number.isFinite(v) ? v : null;
}

/** Sayıyı düzenleme kutusuna yazarken Türkçe ondalık ayracına çevirir. */
function toInput(v: unknown): string {
  return typeof v === "number" && Number.isFinite(v) && v !== 0 ? String(v).replace(".", ",") : "";
}

/**
 * Öneri kutusunun görünümü. Renk TEK BAŞINA yeterli değildir (WCAG 1.4.1);
 * her öneri kutusunun altında ayrıca "öneri — …" metni basılır ve
 * `aria-describedby` ile kutuya bağlanır.
 */
function suggestionClass(on: boolean): string {
  return on ? "border-amber-400 bg-amber-50 ring-1 ring-amber-300" : "";
}

export function CuttingList() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const toast = useToast();

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [skippedNonOrder, setSkippedNonOrder] = useState(0);
  const [sourceless, setSourceless] = useState<SourcelessProduction[]>([]);
  const [invalidKeys, setInvalidKeys] = useState<Set<string>>(new Set());

  const saveAction = useCallableAction<{ productionId: string; lines: SaveLine[] }, SaveResult>(
    CALLABLE_SAVE,
  );

  const statusesQ = useCollection<StatusDoc>(
    () => query(collection(db, "statuses"), where("category", "==", "production")),
    [],
  );
  const statuses = useMemo(
    () =>
      statusesQ.docs
        .filter((s) => s.obsolete !== true)
        .sort((a, b) => statusOrder(a) - statusOrder(b)),
    [statusesQ.docs],
  );
  const current = statuses.find((s) => s.id === status);

  /**
   * Listeyi kur: statüdeki üretim kayıtları → sipariş kalemleri → mevcut kesim satırları.
   * Eski sistem bunu tek dev JOIN ile yapıyordu; Firestore'da üç adımlı okuma olur.
   * Canlı dinleme KULLANILMAZ: kullanıcı formu doldururken satırların altından
   * verinin değişmesi girilen ölçüleri silerdi.
   */
  const load = useCallback(async () => {
    if (!status) {
      setRows([]);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const prodSnap = await getDocs(
        query(collection(db, "production"), where("status", "==", status)),
      );
      const orderProductions = prodSnap.docs.filter((d) => d.get("sourceType") === "order");
      setSkippedNonOrder(prodSnap.size - orderProductions.length);

      const built: Row[] = [];
      const noItems: SourcelessProduction[] = [];

      for (const prod of orderProductions) {
        const orderId = String(prod.get("sourceId") ?? "");
        if (!orderId) continue;

        const [itemSnap, cutSnap] = await Promise.all([
          getDocs(collection(db, "orders", orderId, ITEMS_SUBCOLLECTION)),
          getDocs(collection(db, "production", prod.id, "cuttingList")),
        ]);

        const existing = new Map<string, { docId: string; data: Record<string, unknown> }>();
        for (const c of cutSnap.docs) {
          // ★ Kalem kimliği ÜÇ adla duruyor (kural `orderItemId`, sunucunun eski
          // yazımı `lineItemId`, göç betiği `itemId`). Tek adla arayan ekran,
          // kayıtlı ölçüleri GÖRMEZ ve operatör aynı ölçüyü tekrar girer.
          const data = c.data() as Record<string, unknown>;
          const key = cuttingLineItemId(data as CuttingLineFields) || c.id;
          existing.set(key, { docId: c.id, data });
        }

        const orderLabel =
          String(prod.get("orderName") ?? "") ||
          String(prod.get("sourceLabel") ?? "") ||
          `#${orderId}`;

        /* ★ Kalemi olmayan üretim kaydı SESSİZ GEÇİLMEZ. Canlıda aktif
           statülerdeki 10 üretim kaydının kaynak siparişi `orders` koleksiyonunda
           YOK (göç kaybı); bu ekranda hiç satır üretmiyor ve atölye siparişin
           varlığından haberdar bile olmuyordu. */
        if (itemSnap.empty) {
          noItems.push({ productionId: prod.id, orderId, label: orderLabel });
          continue;
        }

        for (const item of itemSnap.docs) {
          const cur = existing.get(item.id);
          const d = cur?.data ?? {};
          const saved = cur !== undefined;

          const productName = String(item.get("title") ?? item.get("productName") ?? "Ürün");
          const orderQuantity = Number(item.get("quantity") ?? 0);

          /* ★ Referans ölçü: `optionSize ?? variantTitle` yazılıyordu. `??` boş
             dizeyi yakalamaz ve canlı örneklemde 4.000 kalemin 4.000'inde İKİ
             ALAN DA boş dize; sütun her satırda boş çıkıyordu. Artık
             `firstFilled` kullanılıyor ve son çare ürün adındaki ölçü metni. */
          const ref = referenceSizeLabel({
            optionSize: String(item.get("optionSize") ?? ""),
            variantTitle: String(item.get("variantTitle") ?? ""),
            productTitle: productName,
          });

          /* Ön doldurma YALNIZCA kayıtlı satır yokken yapılır — kaydedilmiş
             ölçünün üstüne öneri yazmak veri kaybı olurdu. */
          const parsed = saved ? null : parseSizeFromTitle(productName);
          const suggestQuantity = !saved && orderQuantity > 0;

          built.push({
            key: `${prod.id}::${item.id}`,
            productionId: prod.id,
            orderId,
            orderLabel,
            orderItemId: item.id,
            docId: cur?.docId,
            productName,
            refSize: ref.label,
            refSizeFromTitle: ref.fromTitle,
            orderQuantity,
            size1: saved ? toInput(d.size1) : parsed ? toInput(parsed.size1) : "",
            size2: saved ? toInput(d.size2) : parsed ? toInput(parsed.size2) : "",
            quantity: saved
              ? typeof d.quantity === "number" && d.quantity > 0
                ? String(d.quantity)
                : ""
              : suggestQuantity
                ? String(orderQuantity)
                : "",
            thickness: typeof d.thickness === "number" ? d.thickness : 0,
            originOfMaterial: (d.originOfMaterial as Origin | undefined) ?? "",
            suggestedSize: parsed !== null,
            suggestedQuantity: suggestQuantity,
            dirty: false,
          });
        }
      }

      built.sort(
        (a, b) =>
          a.orderId.localeCompare(b.orderId, "tr-TR") ||
          a.productName.localeCompare(b.productName, "tr-TR"),
      );
      setRows(built);
      setSourceless(noItems);
    } catch (e) {
      console.error("Kesim listesi yüklenemedi", e);
      setLoadError(callableErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Satırı günceller ve OPERATÖR DOKUNDU olarak işaretler.
   * Değiştirilen kutunun "öneri" işareti kalkar: değer artık operatörün.
   */
  const patch = (key: string, change: Partial<Row>) =>
    setRows((rs) =>
      rs.map((r) =>
        r.key === key
          ? {
              ...r,
              ...change,
              dirty: true,
              suggestedSize:
                r.suggestedSize && change.size1 === undefined && change.size2 === undefined,
              suggestedQuantity: r.suggestedQuantity && change.quantity === undefined,
            }
          : r,
      ),
    );

  /** Ekranda en az bir öneri değeri duruyor mu? (uyarı bandı için) */
  const hasSuggestions = rows.some((r) => r.suggestedSize || r.suggestedQuantity);

  /**
   * Kaydet. Şart: ölçü ve adet dolu, malzeme kaynağı seçili.
   * Callable üretim kaydı başına çağrılır (girdi şeması `{productionId, lines[]}`).
   *
   * ★ YALNIZCA OPERATÖRÜN DOKUNDUĞU SATIRLAR gönderilir. Eskiden "herhangi bir
   * kutusu dolu olan" satır gönderiliyordu; ön doldurmayla birlikte bu kural
   * EKRANDAKİ HER SATIRI kaydeder ve türetilmiş öneriler gerçek kesim ölçüsü
   * gibi veritabanına yazılırdı. Malzeme kaynağı hiç ön dolmadığı ve zorunlu
   * olduğu için, kaydedilen her satırda en az bir bilinçli seçim vardır.
   */
  const save = async () => {
    const byProduction = new Map<string, SaveLine[]>();
    const invalid = new Set<string>();

    for (const r of rows) {
      if (!r.dirty) continue;

      const size1 = parseDecimalTr(r.size1);
      const size2 = parseDecimalTr(r.size2);
      const quantity = parseDecimalTr(r.quantity);

      const ok =
        size1 !== null && size1 > 0 && quantity !== null && quantity >= 1 && r.originOfMaterial !== "";
      if (!ok) {
        invalid.add(r.key);
        continue;
      }

      const list = byProduction.get(r.productionId) ?? [];
      list.push({
        ...(r.docId ? { id: r.docId } : {}),
        orderItemId: r.orderItemId,
        size1: size1!,
        size2: size2 ?? 0,
        quantity: Math.round(quantity!),
        thickness: r.thickness,
        originOfMaterial: r.originOfMaterial as Origin,
      });
      byProduction.set(r.productionId, list);
    }

    setInvalidKeys(invalid);

    if (byProduction.size === 0) {
      toast.error(
        invalid.size > 0
          ? `${invalid.size} satır eksik: ölçü, adet ve malzeme kaynağı zorunludur.`
          : "Kaydedilecek değişiklik yok.",
        invalid.size === 0 && hasSuggestions
          ? "Sarı işaretli değerler yalnızca öneridir; kaydedilmeleri için satırı kontrol edip malzeme kaynağını seçin."
          : undefined,
      );
      return;
    }

    const failures: string[] = [];
    let savedLines = 0;

    for (const [productionId, lines] of byProduction) {
      const out = await saveAction.run({ productionId, lines });
      if (!out) {
        failures.push(`${productionId}: ${saveAction.error ?? "kaydedilemedi"}`);
        continue;
      }
      savedLines += out.saved ?? lines.length;
      for (const r of out.results ?? []) {
        if (!r.ok) failures.push(`${r.orderItemId ?? productionId}: ${r.error ?? "hata"}`);
      }
    }

    if (failures.length > 0) {
      toast.error(`${savedLines} satır kaydedildi, ${failures.length} satır kaydedilemedi.`, failures.join("\n"));
    } else {
      toast.success(
        `${savedLines} kesim satırı kaydedildi.`,
        invalid.size > 0 ? `${invalid.size} eksik satır atlandı, ekranda işaretlendi.` : undefined,
      );
    }
    await load();
  };

  /* ── Statü seçilmediyse ─────────────────────────────────────────────────── */
  if (!status) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">
            Kesim Listesi
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Hangi üretim statüsünün kesim listesi hazırlanacak?
          </p>
        </div>

        {statusesQ.loading ? (
          <LoadingBlock />
        ) : statuses.length === 0 ? (
          <EmptyState title="Üretim statüsü tanımlı değil" tone="warning" />
        ) : (
          /* ★ Kayıt sayısı Üretim ekranında vardı, burada yoktu: atölye hangi
             statüde kaç iş olduğunu görmeden kart seçiyor, çoğu zaman boş
             listeye düşüyordu. `onlyOrders` — kesim yalnızca siparişten doğan
             üretimleri işler, adet de aynı süzgeçle sayılır. */
          <StatusCountCards
            statuses={statuses}
            hrefFor={(id) => `/cutting-list?status=${encodeURIComponent(id)}`}
            onlyOrders
          />
        )}
      </div>
    );
  }

  let lastOrderId = "";

  return (
    <div className="space-y-5">
      <div>
        <nav aria-label="Sayfa yolu" className="text-sm noprint">
          <ol role="list" className="flex items-center gap-2 text-gray-500">
            <li>
              <Link to="/production" className="hover:text-gray-700">
                Üretim
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-700">Kesim Listesi</li>
          </ol>
        </nav>

        <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">
            Kesim Listesi — {current?.label ?? status}
          </h1>

          <div className="flex flex-wrap items-center gap-2 noprint">
            <label htmlFor="cut-status" className="sr-only">
              Statü
            </label>
            <select
              id="cut-status"
              className="select w-auto"
              value={status}
              onChange={(e) => setParams({ status: e.target.value })}
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label ?? s.id}
                </option>
              ))}
            </select>
            <button type="button" className="btn-secondary" onClick={() => window.print()}>
              Yazdır
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={saveAction.pending || rows.length === 0}
              onClick={() => void save()}
            >
              {saveAction.pending ? "Kaydediliyor…" : "Kaydet"}
            </button>
            {saveAction.pending ? <Spinner size="sm" label="Kaydediliyor" /> : null}
          </div>
        </div>
      </div>

      {skippedNonOrder > 0 ? (
        <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700 noprint">
          {skippedNonOrder} kayıt (SSH / stok siparişi) kesim listesine girmez — kesim yalnızca
          siparişten doğan üretimler içindir.
        </p>
      ) : null}

      {/* ★ Kaynağı kayıp üretim kayıtları — listeden sessizce düşmez. */}
      {sourceless.length > 0 ? (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 noprint">
          <p className="font-semibold">
            {sourceless.length} üretim kaydının sipariş kalemleri okunamadı — kesim satırı
            girilemiyor.
          </p>
          <p className="mt-1">
            Kaynak sipariş veritabanında bulunmuyor (eski sistemden göç sırasında kaybolmuş). Bu
            kayıtlar için kesim ölçüsü sisteme girilemez.{" "}
            <span className="font-semibold">Yapılacak:</span> ofise bildirin, ölçüleri eski sistem
            ya da sipariş kâğıdı üzerinden teyit ettirin.
          </p>
          <ul role="list" className="mt-2 flex flex-wrap gap-2">
            {sourceless.map((s) => (
              <li key={s.productionId} className="rounded bg-white px-2 py-1 text-xs ring-1 ring-red-200">
                {s.label} <span className="text-red-700">(sipariş #{s.orderId} yok)</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* ★ ÖNERİ BANDI — ön dolu değerlerin ne olduğu ve ne olmadığı. */}
      {hasSuggestions ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 noprint">
          <p className="font-semibold">Sarı kutulardaki değerler ÖNERİDİR — kesinleşmiş ölçü değildir.</p>
          <p className="mt-1">
            Adet sipariş adedinden, ölçü ise ÜRÜN ADINDAKİ metinden türetildi. Kontrol edip
            düzeltin. Bir satır, siz o satırda bir kutuya dokunmadan{" "}
            <span className="font-semibold">kaydedilmez</span>; malzeme kaynağı zaten zorunludur ve
            hiçbir zaman ön dolmaz.
          </p>
        </div>
      ) : null}

      {loadError ? (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
          {loadError}
        </div>
      ) : null}

      {loading ? (
        <LoadingBlock label="Kesim listesi hazırlanıyor" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Bu statüde kesilecek kalem yok"
          description="Siparişten doğan üretim kaydı bulunamadı."
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="table-dense">
            <caption className="sr-only">
              {current?.label ?? status} statüsündeki siparişlerin kesim ölçüleri
            </caption>
            <thead>
              <tr>
                <th scope="col">Sipariş</th>
                <th scope="col">Ürün</th>
                <th scope="col">Ölçü</th>
                <th scope="col">Adet</th>
                <th scope="col">mm / Malzeme</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const newOrder = r.orderId !== lastOrderId;
                lastOrderId = r.orderId;
                const bad = invalidKeys.has(r.key);
                const sizeHintId = `cut-size-hint-${r.key}`;
                const qtyHintId = `cut-qty-hint-${r.key}`;

                return (
                  <tr
                    key={r.key}
                    className={`${newOrder ? "border-t-2 border-t-gray-800" : ""} ${
                      bad ? "bg-red-50" : ""
                    }`}
                  >
                    <td className="whitespace-nowrap">
                      {newOrder ? (
                        <Link className="link" to={`/orders/${encodeURIComponent(r.orderId)}`}>
                          {r.orderLabel}
                        </Link>
                      ) : null}
                    </td>

                    {/* ★ TABLET: ürün sütununa taban genişlik. Tablet dikeyde
                        (768px) sütun ~90px'e sıkışıyor, "Bely 3 Raflı Kitaplık -
                        180 x 38 cm" kelime kelime alt alta sarıp satırı 8 satır
                        yüksekliğe çıkarıyordu; kâğıt da öyle basılıyordu. */}
                    <td className="min-w-[11rem]">
                      <span className="block text-sm text-gray-900">{r.productName}</span>
                      <span className="block text-xs text-gray-600">
                        {/* ★ Referans ölçü: eskiden `optionSize ?? variantTitle`
                            okunuyordu ve HER SATIRDA boştu. Artık boş dize de
                            eksik sayılır, son çare ürün adındaki ölçüdür. */}
                        {r.refSize ? (
                          <>
                            ölçü: <span className="font-medium text-gray-800">{r.refSize}</span>
                            {r.refSizeFromTitle ? (
                              <span className="text-gray-500"> (ürün adından)</span>
                            ) : null}
                            {" · "}
                          </>
                        ) : (
                          <span className="text-gray-500">ölçü bilgisi yok · </span>
                        )}
                        sipariş adedi: {n(r.orderQuantity)}
                      </span>
                      {bad ? (
                        <span role="alert" className="block text-xs font-semibold text-red-700 noprint">
                          Ölçü, adet ve malzeme kaynağı zorunludur — bu satır kaydedilmedi.
                        </span>
                      ) : null}
                    </td>

                    <td className="whitespace-nowrap">
                      <span className="flex items-center gap-1 noprint">
                        <input
                          type="text"
                          inputMode="decimal"
                          lang="tr"
                          aria-label={`${r.productName} 1. ölçü${r.suggestedSize ? " (öneri)" : ""}`}
                          aria-describedby={r.suggestedSize ? sizeHintId : undefined}
                          /* ★ TABLET: 5 sütun 747px yer istiyordu, tablet dikeyde
                             kap 720px — tablo yatay kayıyordu. Ölçü kutuları dar
                             ekranda 64px, masaüstünde (lg+) eski 80px. Yükseklik
                             py-3 ile 44px üstünde kalır. */
                          className={`input w-16 py-3 text-center tabular-nums lg:w-20 ${suggestionClass(r.suggestedSize)}`}
                          value={r.size1}
                          onChange={(e) => patch(r.key, { size1: e.target.value })}
                        />
                        <span aria-hidden="true">x</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          lang="tr"
                          aria-label={`${r.productName} 2. ölçü${r.suggestedSize ? " (öneri)" : ""}`}
                          aria-describedby={r.suggestedSize ? sizeHintId : undefined}
                          className={`input w-16 py-3 text-center tabular-nums lg:w-20 ${suggestionClass(r.suggestedSize)}`}
                          value={r.size2}
                          onChange={(e) => patch(r.key, { size2: e.target.value })}
                        />
                      </span>
                      {/* Etiket KISA tutuldu: tablet dikeyde uzun metin sütunu
                          genişletip tabloyu yatay kaydırıyordu. Kaynağı ("ürün
                          adından") Ürün sütunundaki referans satırı yazıyor,
                          ayrıntısı `aria-label` ve uyarı bandında. */}
                      {r.suggestedSize ? (
                        <span
                          id={sizeHintId}
                          className="mt-1 inline-flex rounded bg-amber-200 px-1.5 py-0.5 text-xs font-semibold text-amber-900 noprint"
                        >
                          öneri
                        </span>
                      ) : null}
                      {/* Yazdırılan kâğıtta öneri, kesinleşmiş ölçüyle karışmasın. */}
                      <span className="print-only">
                        {r.size1}x{r.size2}
                        {r.suggestedSize ? " (öneri)" : ""}
                      </span>
                    </td>

                    <td className="whitespace-nowrap">
                      <input
                        type="text"
                        inputMode="numeric"
                        aria-label={`${r.productName} kesim adedi${r.suggestedQuantity ? " (öneri)" : ""}`}
                        aria-describedby={r.suggestedQuantity ? qtyHintId : undefined}
                        className={`input w-16 py-3 text-center tabular-nums noprint lg:w-20 ${suggestionClass(r.suggestedQuantity)}`}
                        value={r.quantity}
                        onChange={(e) => patch(r.key, { quantity: e.target.value })}
                      />
                      {r.suggestedQuantity ? (
                        <span
                          id={qtyHintId}
                          className="mt-1 inline-flex rounded bg-amber-200 px-1.5 py-0.5 text-xs font-semibold text-amber-900 noprint"
                        >
                          öneri
                        </span>
                      ) : null}
                      <span className="print-only">
                        {r.quantity}
                        {r.suggestedQuantity ? " (öneri)" : ""}
                      </span>
                    </td>

                    <td className="whitespace-nowrap">
                      {/* ★ TABLET: iki açılır liste yan yana ~190px istiyor ve o
                          genişlik doğrudan Ürün sütunundan çıkıyordu. Tablet ve
                          altında ALT ALTA, masaüstünde (lg+) eskisi gibi yan yana. */}
                      <span className="flex flex-col gap-2 noprint lg:flex-row lg:items-center">
                        <label className="sr-only" htmlFor={`th-${r.key}`}>
                          Kalınlık
                        </label>
                        <select
                          id={`th-${r.key}`}
                          className="select w-auto py-3"
                          value={r.thickness}
                          onChange={(e) => patch(r.key, { thickness: Number(e.target.value) })}
                        >
                          {THICKNESS_OPTIONS.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>

                        <label className="sr-only" htmlFor={`or-${r.key}`}>
                          Malzeme kaynağı
                        </label>
                        <select
                          id={`or-${r.key}`}
                          className="select w-auto py-3"
                          value={r.originOfMaterial}
                          onChange={(e) =>
                            patch(r.key, { originOfMaterial: e.target.value as Origin | "" })
                          }
                        >
                          <option value="">Kaynak seç</option>
                          {ORIGIN_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </span>
                      <span className="print-only">
                        {(r.thickness ? `${r.thickness} mm, ` : "") +
                          (ORIGIN_OPTIONS.find((o) => o.value === r.originOfMaterial)?.label ?? "")}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 ? (
        <div className="noprint">
          <button
            type="button"
            className="btn-primary"
            disabled={saveAction.pending}
            onClick={() => void save()}
          >
            {saveAction.pending ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default CuttingList;
